



























































































































































































import { QueryBuilder } from '@/app/components';
import { SampleValues } from '@/modules/data-checkin/components';
import { useCleaningConstraints } from '@/modules/data-checkin/composable';
import { ExclamationCircleIcon } from '@vue-hero-icons/outline';
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import * as R from 'ramda';
import OutliersRule from '../OutliersRule.vue';
import { Condition, Constraint } from '../cleaning.types';
import EditConstraint from './EditConstraint.vue';
import ViewConstraint from './ViewConstraint.vue';
import ConstraintErrors from './ConstraintErrors.vue';
import { useCleaning } from '../../../composable';

export default defineComponent({
    name: 'EditAdvancedContstraint',
    components: {
        QueryBuilder,
        EditConstraint,
        ViewConstraint,
        OutliersRule,
        SampleValues,
        ExclamationCircleIcon,
        ConstraintErrors,
    },
    props: {
        configuration: {
            type: Object,
            required: true,
        },
        constraint: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: null,
        },
        selectedFieldsTypes: {
            type: Array,
            required: true,
        },
        previousStepSample: {
            type: Array,
            default: null,
        },
        sample: {
            type: Array,
            required: true,
        },
        multipleOutlierFields: {
            type: Boolean,
            default: false,
        },
        canRunManyTimes: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const editingConstraint = ref<boolean>(false);
        const editableConstraint = ref<any>(props.constraint);
        const selectedFields = ref<any[]>([]);
        const isNew = ref<boolean>(editableConstraint.value.structure.conditions.length === 0);
        const sameConditionsInGroup = ref<boolean>(false);
        const emptyGroups = ref<boolean>(false);
        const multipleSelectedField = ref<boolean>(false);
        const typesRef = computed<string[] | any>(() => props.selectedFieldsTypes);

        const {
            selectedFieldsTypes: involvedFieldsTypes,
            availableConstraintOptions,
            alreadyDefinedConstraintTypes,
            referencedFields,
        } = useCleaningConstraints(props.configuration, selectedFields);

        const canCreateConstraint = computed(() => {
            if (editingConstraint.value || sameConditionsInGroup.value || emptyGroups.value) return false;
            if (
                !editableConstraint.value.structure?.conditions?.length ||
                !editableConstraint.value?.outliersRule?.type
            )
                return false;
            if (editableConstraint.value.outliersRule.type) {
                if (
                    editableConstraint.value.outliersRule.type === 'DEFAULT_VALUE' &&
                    (editableConstraint.value.outliersRule.replaceValue === null ||
                        editableConstraint.value.outliersRule.replaceValue === '')
                ) {
                    return false;
                }
                return !(
                    editableConstraint.value.outliersRule.type === 'PREVIOUS_VALUE' &&
                    (editableConstraint.value.outliersRule.secondaryRule.replaceValue === null ||
                        editableConstraint.value.outliersRule.secondaryRule.replaceValue === '')
                );
            }
            return true;
        });

        const newConditionTemplate = ref({
            fieldName: null,
            multipleOperator: 'none',
            type: null,
            details: null,
        });

        const newGroupTemplate = ref({
            operator: 'AND',
            conditions: [],
        });

        const themeCalculator = (query: any) => {
            switch (query.operator) {
                case 'AND':
                    return 'purple';
                case 'OR':
                    return 'teal';
                default:
                    return null;
            }
        };

        const multipleOperatorOptions = [
            {
                value: 'element',
                title: 'Same element position',
                description:
                    'The values of the specific field that are in the same array position (element) in each record need to satisfy the cleaning rule',
                available: props.multipleOutlierFields,
            },
            {
                value: 'all',
                title: 'All array values',
                description:
                    'All values of the specific field (across all array elements) in each record need to satisfy the cleaning rule',
                available: true,
            },
            {
                value: 'any',
                title: 'Any array value',
                description:
                    'A value of the specific field in any array position (element) in each record should satisfy the cleaning rule',
                available: true,
            },
        ];

        const hasConditions = computed(() => editableConstraint.value.structure?.conditions?.length);

        const fieldChanged = (fieldName: string, query?: any) => {
            if (query) {
                query.details = null;
                query.type = null;
                query.multipleOperator = 'none';
                query.parentFieldName = props.constraint?.fieldName?.replaceAll('__', '.');
                query.fieldType = getFieldType(fieldName)[0];
            }
            selectedFields.value = [];
            multipleSelectedField.value = false;
            const foundField = props.configuration.fields.find((f: any) => f.name === fieldName);
            if (foundField) {
                selectedFields.value = [foundField];
                multipleSelectedField.value =
                    !!foundField.multiple || foundField.path.some((pathPart: string) => pathPart.endsWith('[]'));
            }
        };

        const { setDateTime, parseTimeStringToDate } = useCleaning(typesRef);

        const saveConstraint = () => {
            if (
                (props.selectedFieldsTypes.includes('datetime') ||
                    props.selectedFieldsTypes.includes('date') ||
                    props.selectedFieldsTypes.includes('time')) &&
                editableConstraint.value.outliersRule?.replaceValue
            ) {
                editableConstraint.value.outliersRule.replaceValue = setDateTime(
                    editableConstraint.value.outliersRule.replaceValue as string,
                );
            }

            if (isNew.value) {
                emit('save-create');
            } else {
                emit('save-update');
            }
        };

        const checkGroup = (conditions: Condition[]) => {
            const nonAdvancedConditions = conditions
                .filter((c) => c.operator === undefined)
                .map((c) => {
                    const result = R.clone(c);
                    delete result.conditionUid;
                    return result;
                })
                .map((c) => JSON.stringify(c));
            if (nonAdvancedConditions.length !== new Set(nonAdvancedConditions).size) {
                sameConditionsInGroup.value = true;
            }
            conditions.forEach((c) => {
                if (c.conditions !== undefined) {
                    if (c.conditions.length > 0) checkGroup(c.conditions);
                    else emptyGroups.value = true;
                }
            });
        };

        const handleEmptyConditions = (isRoot: boolean) => {
            if (isRoot) emit('delete-constraint');
        };

        const getFieldType = (fieldName: string) => {
            if (!fieldName) return [];
            const field = props.configuration.fields.find((f: any) => f.name === fieldName);
            return field ? [field.type] : [];
        };

        const decimalPlacesError = computed(() => {
            const errorsArray: string[] = [];

            if (props.selectedFieldsTypes.includes('double') || props.selectedFieldsTypes.includes('float')) {
                const fieldsToCheck = [
                    editableConstraint.value.outliersRule?.replaceValue,
                    editableConstraint.value.outliersRule?.secondaryRule?.replaceValue,
                ];

                // current precision is 7 decimal places
                const precision = Math.pow(10, 7);

                const hasNoMoreThan7DecimalPlaces = (value: string) => {
                    return Math.round(Number(value) * precision) / precision === Number(value);
                };

                fieldsToCheck.forEach((field) => {
                    if (field && !hasNoMoreThan7DecimalPlaces(field as string)) {
                        errorsArray.push(
                            `Invalid number <span class="font-bold">"${field}"</span>. Must have no more than 7 decimal places.`,
                        );
                    }
                });
            }
            return R.uniq(errorsArray);
        });

        const integerError = computed(() => {
            const errorsArray: string[] = [];

            if (props.selectedFieldsTypes.includes('integer')) {
                const fieldsToCheck = [
                    editableConstraint.value.outliersRule?.replaceValue,
                    editableConstraint.value.outliersRule?.secondaryRule?.replaceValue,
                ];

                fieldsToCheck.forEach((field) => {
                    if (field && !Number.isInteger(Number(field))) {
                        errorsArray.push(
                            `Invalid number <span class="font-bold">"${field}"</span>. Must be an integer.`,
                        );
                    }
                });
            }
            return R.uniq(errorsArray);
        });

        const errors = computed(() => {
            return [...decimalPlacesError.value, ...integerError.value];
        });

        if (!isNew.value) {
            const { outliersRule } = editableConstraint.value;
            if (props.selectedFieldsTypes.includes('time')) {
                if (outliersRule?.replaceValue) {
                    outliersRule.replaceValue = parseTimeStringToDate(outliersRule.replaceValue as string);
                } else if (outliersRule?.secondaryRule?.replaceValue) {
                    outliersRule.secondaryRule.replaceValue = parseTimeStringToDate(
                        outliersRule.secondaryRule.replaceValue as string,
                    );
                }
            }
        }

        watch(editableConstraint.value, (constraint: Constraint) => {
            if (constraint.structure?.conditions?.length) {
                sameConditionsInGroup.value = false;
                emptyGroups.value = false;
                checkGroup(constraint.structure.conditions);
            }
        });

        return {
            isNew,
            editingConstraint,
            editableConstraint,
            newConditionTemplate,
            newGroupTemplate,
            themeCalculator,
            alreadyDefinedConstraintTypes,
            availableConstraintOptions,
            referencedFields,
            hasConditions,
            canCreateConstraint,
            selectedFields,
            fieldChanged,
            involvedFieldsTypes,
            saveConstraint,
            emptyGroups,
            sameConditionsInGroup,
            handleEmptyConditions,
            getFieldType,
            multipleSelectedField,
            multipleOperatorOptions,
            errors,
        };
    },
});
